import { SpecError } from "../errors";
import type { RawRow } from "../types";
import type { WarningCollector } from "../warnings/warning-collector";
import { DetailSheetParser } from "./detail-sheet-parser";
import { ListSheetParser } from "./list-sheet-parser";
import type { ParsedItemContext, ParsedSpec } from "./types";

const LIST_SHEET_NAMES = {
  service: "サービスリスト",
  view: "画面リスト",
  component: "共通コンポーネントリスト",
} as const;

const DETAIL_SHEET_PREFIXES = {
  view: "画面_",
  component: "共通_",
} as const;

/**
 * Phase 2: シート Map を中間表現 `ParsedSpec` に変換する Orchestrator。
 *
 * - 一覧シートは `ListSheetParser` に委譲
 * - 詳細シート (`画面_*` / `共通_*`) は `DetailSheetParser` に委譲
 * - それ以外のシート (`説明` / `凡例` 等) は無視
 *
 * `サービスリスト` と `画面リスト` の両方が欠落している場合は構造的にパース不可能
 * とみなして `SpecError("MISSING_REQUIRED_SHEET")` を throw する。
 */
export class SpecParser {
  private readonly listParser: ListSheetParser;
  private readonly detailParser: DetailSheetParser;

  constructor(warnings: WarningCollector) {
    this.listParser = new ListSheetParser();
    this.detailParser = new DetailSheetParser(warnings);
  }

  parse(sheets: Map<string, RawRow[]>): ParsedSpec {
    if (
      !sheets.has(LIST_SHEET_NAMES.service) &&
      !sheets.has(LIST_SHEET_NAMES.view)
    ) {
      throw new SpecError(
        "MISSING_REQUIRED_SHEET",
        `Required sheets "${LIST_SHEET_NAMES.service}" and "${LIST_SHEET_NAMES.view}" are both missing.`,
        { missingSheets: [LIST_SHEET_NAMES.service, LIST_SHEET_NAMES.view] },
      );
    }

    const services = this.listParser.parseService(sheets.get(LIST_SHEET_NAMES.service) ?? []);
    const views = this.listParser.parseView(sheets.get(LIST_SHEET_NAMES.view) ?? []);
    const components = this.listParser.parseComponent(
      sheets.get(LIST_SHEET_NAMES.component) ?? [],
    );

    const itemsByContext: ParsedItemContext[] = [];
    for (const [name, rows] of Array.from(sheets)) {
      if (name.startsWith(DETAIL_SHEET_PREFIXES.view)) {
        itemsByContext.push(this.detailParser.parse(name, rows, "view"));
      } else if (name.startsWith(DETAIL_SHEET_PREFIXES.component)) {
        itemsByContext.push(this.detailParser.parse(name, rows, "component"));
      }
    }

    return { services, views, components, itemsByContext };
  }
}
